import { useMemo } from 'react';
import Badge from '@mui/material/Badge';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';

import { getMessage } from '@gef-ui/localization/messages';
import useThemeColor from '@gef-ui/material/hooks/useThemeColor';

import { ComponentLoader } from 'components/index';
import fetch from 'features/common/fetch';
import { getInfoGraphicsData } from 'features/views/dashboard/statistics/selectors';

import HalfDoughnutChartTemplate from './HalfDoughnutChartTemplate';

export const ContactsGraph = () => {
	const success = useThemeColor('success');
	const warning = useThemeColor('warning');
	const error = useThemeColor('error');

	const backgroundColor = useMemo(() => [success, warning, error], [success, warning, error]);

	const { contacts = {} } = useSelector(getInfoGraphicsData);

	const orderedData = [contacts?.contactsDone, contacts?.contactsInProgress, contacts?.contactsReleased];

	const data = {
		backgroundColor,
		values: orderedData,
		sumCount: contacts?.contactsSummary,
	};

	const badgeSx = {
		'& .MuiBadge-dot': {
			marginTop: 1.25,
			marginRight: 2,
		},
	};

	return (
		<ComponentLoader fetchStorePath={fetch.constants.urls.contactsSummary} forceInlineError>
			<Paper
				sx={{
					p: 3,
					backgroundColor: (theme) => theme.palette.background[100],
					borderRadius: 1,
					height: '198px',
				}}
			>
				<Grid container>
					<Grid size={12}>
						<Grid container>
							<Grid size={5}>
								<HalfDoughnutChartTemplate
									id={getMessage('dashboard:statistics.contacts')}
									data={data}
								/>
							</Grid>
							<Grid
								sx={{
									pl: 4,
								}}
								size={7}
							>
								<Stack
									spacing={1}
									sx={{
										justifyContent: 'center',
										height: '100%',
									}}
								>
									<Stack
										direction="row"
										sx={{
											justifyContent: 'space-between',
										}}
									>
										<Stack direction="row">
											<Badge sx={{ ...badgeSx }} variant="dot" color="success" />
											<Typography variant="body1">
												{getMessage('dashboard:statistics.processed')}
											</Typography>
										</Stack>
										<Typography
											variant="body1"
											sx={{
												fontWeight: 'bold',
											}}
										>
											{contacts?.contactsDone}
										</Typography>
									</Stack>
									<Stack
										direction="row"
										sx={{
											justifyContent: 'space-between',
										}}
									>
										<Stack direction="row">
											<Badge sx={{ ...badgeSx }} variant="dot" color="warning" />
											<Typography variant="body1">
												{getMessage('dashboard:statistics.drafts')}
											</Typography>
										</Stack>
										<Typography
											variant="body1"
											sx={{
												fontWeight: 'bold',
											}}
										>
											{contacts?.contactsInProgress}
										</Typography>
									</Stack>
									<Stack
										direction="row"
										sx={{
											justifyContent: 'space-between',
										}}
									>
										<Stack direction="row">
											<Badge sx={{ ...badgeSx }} variant="dot" color="error" />
											<Typography variant="body1">
												{getMessage('dashboard:statistics.toProcess')}
											</Typography>
										</Stack>
										<Typography
											variant="body1"
											sx={{
												fontWeight: 'bold',
											}}
										>
											{contacts?.contactsReleased}
										</Typography>
									</Stack>
								</Stack>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Paper>
		</ComponentLoader>
	);
};

export default ContactsGraph;
